import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Check, FileText, Loader2, Trash2 } from '@/components/icons';
import { coverageOf, isJobRunning, type Requirement, type RequirementSetSummary, type RequirementSetView, type TestDraft } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { stepForStatus, type WizardStep } from '@/lib/requirements';
import { Badge, Button, Card, CardHeader, ErrorNotice, PageHeader, PageLoader, useConfirm, useToast } from '@/components/ui';
import { UploadForm, type UploadInput } from '@/components/requirements/UploadForm';
import { RequirementsStep } from '@/components/requirements/RequirementsStep';
import { DraftsStep } from '@/components/requirements/DraftsStep';
import { SaveStep, type SaveResult } from '@/components/requirements/SaveStep';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'documents', label: 'Documents' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'drafts', label: 'Test cases' },
  { id: 'save', label: 'Save' },
];

const STATUS_LABEL: Record<RequirementSetSummary['status'], { label: string; tone: 'muted' | 'info' | 'warning' | 'success' }> = {
  NEW: { label: 'Uploaded', tone: 'muted' },
  REQUIREMENTS: { label: 'Requirements found', tone: 'info' },
  DRAFTS: { label: 'Test cases drafted', tone: 'warning' },
  SAVED: { label: 'Tests saved', tone: 'success' },
};

function Stepper({ step, reached, onStep }: { step: WizardStep; reached: Set<WizardStep>; onStep: (s: WizardStep) => void }) {
  const at = STEPS.findIndex((s) => s.id === step);
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Steps">
      {STEPS.map((s, i) => {
        const done = i < at;
        const can = reached.has(s.id) && s.id !== 'documents';
        return (
          <li key={s.id} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!can}
              onClick={() => onStep(s.id)}
              aria-current={s.id === step ? 'step' : undefined}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition',
                s.id === step ? 'bg-primary text-primary-fg' : done ? 'bg-primary-subtle text-fg' : 'bg-surface-raised text-fg-muted',
                can && s.id !== step && 'hover:ring-1 hover:ring-primary',
              )}
            >
              <span className="grid size-5 place-items-center rounded-full bg-black/10 text-xs font-semibold tabular-nums">{done ? <Check className="size-3" /> : i + 1}</span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && <span className="h-px w-6 bg-border" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

function JobPanel({ set, onRetry }: { set: RequirementSetView; onRetry: () => void }) {
  const job = set.job!;
  const what = job.kind === 'requirements' ? 'Finding the requirements' : 'Writing the test cases';
  if (job.error)
    return (
      <Card className="p-5" role="alert">
        <p className="text-sm font-semibold text-fg">{what} did not finish</p>
        <p className="mt-1 text-sm text-fg-secondary">{job.error}</p>
        {set.canEdit && (
          <Button className="mt-3" size="sm" variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Card>
    );
  const parts = job.kind === 'requirements' ? 'document parts' : 'requirement batches';
  return (
    <Card className="flex items-center gap-4 p-5" role="status" data-testid="requirements-job">
      <Loader2 className="size-5 animate-spin text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{what}…</p>
        <p className="text-xs text-fg-secondary">
          The AI model is reading {job.total > 1 ? `${parts}: ${job.done} of ${job.total} done` : 'it'}. This can take a minute or two; you can leave this page and come back.
        </p>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-raised">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.max(8, (job.done / Math.max(1, job.total)) * 100)}%` }} />
        </div>
      </div>
    </Card>
  );
}

/**
 * Local copy of the set's requirements / drafts: edits show at once and are saved (PATCH) shortly after; server
 * data replaces the copy whenever nothing is waiting to be saved.
 */
function useSetEditor(set: RequirementSetView | undefined) {
  const qc = useQueryClient();
  const toast = useToast();
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [drafts, setDrafts] = useState<TestDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const pending = useRef<{ requirements?: Requirement[]; drafts?: TestDraft[] }>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!set || timer.current || saving) return;
    setRequirements(set.requirements);
    setDrafts(set.drafts);
  }, [set, saving]);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const patch = pending.current;
    pending.current = {};
    if (!set || (!patch.requirements && !patch.drafts)) return;
    setSaving(true);
    try {
      const next = await api.updateRequirementSet(set.id, patch);
      qc.setQueryData(['requirement-set', set.id], next);
    } catch (err) {
      toast.error(errorMessage(err));
      await qc.invalidateQueries({ queryKey: ['requirement-set', set.id] });
    } finally {
      setSaving(false);
    }
  }, [set, qc, toast]);

  const schedule = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), 800);
  };

  return {
    requirements,
    drafts,
    saving: saving || !!timer.current,
    flush,
    setRequirements: (next: Requirement[]) => {
      setRequirements(next);
      pending.current.requirements = next;
      schedule();
    },
    setDrafts: (next: TestDraft[]) => {
      setDrafts(next);
      pending.current.drafts = next;
      schedule();
    },
  };
}

function SetWizard({ setId, projectId }: { setId: string; projectId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const query = useQuery({
    queryKey: ['requirement-set', setId],
    queryFn: () => api.requirementSet(setId),
    refetchInterval: (q) => (isJobRunning(q.state.data?.job) ? 2000 : false),
  });
  const set = query.data;
  const editor = useSetEditor(set);
  const [step, setStep] = useState<WizardStep | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);
  const running = isJobRunning(set?.job);
  const lastJob = useRef<'requirements' | 'drafts' | null>(null);

  // When a job ends well, open the step that shows its result.
  useEffect(() => {
    if (!set) return;
    if (lastJob.current && !running && !set.job) setStep(lastJob.current === 'drafts' ? 'drafts' : 'requirements');
    lastJob.current = running ? set.job!.kind : null;
    if (step === null) setStep(stepForStatus(set.status));
  }, [set, running, step]);

  const store = (next: RequirementSetView) => {
    qc.setQueryData(['requirement-set', setId], next);
    void qc.invalidateQueries({ queryKey: ['requirement-sets', projectId] });
  };
  const analyze = useMutation({
    mutationFn: async () => {
      await editor.flush();
      return api.analyzeRequirements(setId);
    },
    onSuccess: store,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const generate = useMutation({
    mutationFn: async (body: { keys?: string[]; replace?: boolean }) => {
      await editor.flush();
      return api.generateDrafts(setId, body);
    },
    onSuccess: store,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const save = useMutation({
    mutationFn: async (groupName: string | null) => {
      await editor.flush();
      return api.saveDrafts(setId, { draftIds: editor.drafts.filter((d) => d.status === 'accepted' && !d.savedTestId).map((d) => d.id), ...(groupName ? { groupName } : {}) });
    },
    onSuccess: (r) => {
      store(r.set);
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['tests', projectId] });
      void qc.invalidateQueries({ queryKey: ['groups', projectId] });
      if (r.saved.length) toast.success(`${r.saved.length} ${r.saved.length === 1 ? 'test' : 'tests'} saved`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const coverage = useMemo(() => coverageOf(editor.requirements, editor.drafts), [editor.requirements, editor.drafts]);

  if (query.isPending || project.isPending) return <PageLoader />;
  if (query.isError) return <ErrorNotice error={query.error} onRetry={() => query.refetch()} />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  const s = set!;
  const reached = new Set<WizardStep>(['documents', 'requirements', ...(editor.drafts.length ? (['drafts', 'save'] as const) : [])]);
  const current = step ?? 'requirements';

  return (
    <div className="space-y-6">
      <Link to={`/projects/${projectId}/requirements`} className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="size-4" /> Business documents · {project.data.name}
      </Link>
      <PageHeader
        eyebrow="Tests from business documents"
        title={s.name}
        description={`Uploaded by ${s.createdBy.name} ${timeAgo(s.createdAt)}.${s.canEdit ? '' : ' Only the person who uploaded it or a Project Admin can change it.'}`}
        actions={<span className="text-xs text-fg-muted">{editor.saving ? 'Saving…' : s.canEdit ? 'All changes saved' : ''}</span>}
      />
      <Stepper step={current} reached={reached} onStep={(x) => setStep(x)} />
      {s.job && <JobPanel set={s} onRetry={() => (s.job?.kind === 'drafts' ? generate.mutate({}) : analyze.mutate())} />}
      {!running && current === 'requirements' && (
        <RequirementsStep
          set={s}
          requirements={editor.requirements}
          onChange={editor.setRequirements}
          onAnalyze={() => analyze.mutate()}
          onGenerate={() => generate.mutate({})}
          analyzing={analyze.isPending}
          generating={generate.isPending}
          coverage={coverage}
        />
      )}
      {!running && current === 'drafts' && (
        <DraftsStep
          set={s}
          requirements={editor.requirements}
          drafts={editor.drafts}
          web={project.data.platform === 'WEB'}
          onChange={editor.setDrafts}
          onGenerate={() => generate.mutate({})}
          onRegenerate={(key) => generate.mutate({ keys: [key], replace: true })}
          onNext={() => {
            setResult(null);
            setStep('save');
          }}
          generating={generate.isPending}
          coverage={coverage}
        />
      )}
      {!running && current === 'save' && <SaveStep set={s} drafts={editor.drafts} saving={save.isPending} result={result} onSave={(g) => save.mutate(g)} onBack={() => setStep('drafts')} />}
    </div>
  );
}

function SetList({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const sets = useQuery({ queryKey: ['requirement-sets', projectId], queryFn: () => api.requirementSets(projectId) });
  const upload = useMutation({
    mutationFn: async (input: UploadInput) => {
      const set = await api.uploadRequirements(projectId, input);
      try {
        return await api.analyzeRequirements(set.id);
      } catch (err) {
        // Uploaded but the model could not start (e.g. no AI model): open the set, the error says why.
        toast.error(errorMessage(err));
        return set;
      }
    },
    onSuccess: (set) => {
      qc.setQueryData(['requirement-set', set.id], set);
      void qc.invalidateQueries({ queryKey: ['requirement-sets', projectId] });
      navigate(`/projects/${projectId}/requirements/${set.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteRequirementSet(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['requirement-sets', projectId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (project.isPending) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  const automation = ['WEB', 'ANDROID', 'IOS'].includes(project.data.platform);

  return (
    <div className="space-y-6">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="size-4" /> {project.data.name}
      </Link>
      <PageHeader
        eyebrow="New test"
        title="Tests from business documents"
        description="Turn a BRD, user stories or a specification from the business team into reviewed, ready-to-run tests: the AI model finds the requirements, drafts positive, negative and edge-case tests, and you decide what is saved."
      />
      {!automation ? (
        <Card className="p-6 text-sm text-fg-secondary">Tests from business documents are for web and mobile projects.</Card>
      ) : (
        <>
          <ol className="grid gap-3 text-sm sm:grid-cols-4">
            {[
              ['Documents', 'Upload PDF / Word / text, or paste stories'],
              ['Requirements', 'The AI lists them; you review and pick'],
              ['Test cases', 'Positive, negative and edge cases to review'],
              ['Save', 'Accepted tests, linked to their requirements'],
            ].map(([t, d], i) => (
              <li key={t} className="rounded-card bg-surface p-4 ring-1 ring-border ring-inset">
                <span className="text-xs font-semibold tracking-label text-primary uppercase">Step {i + 1}</span>
                <span className="mt-1 block font-semibold text-fg">{t}</span>
                <span className="mt-0.5 block text-xs text-fg-secondary">{d}</span>
              </li>
            ))}
          </ol>
          <UploadForm busy={upload.isPending} onSubmit={(input) => upload.mutate(input)} />
        </>
      )}
      {(sets.data?.length ?? 0) > 0 && (
        <Card>
          <CardHeader title="Earlier uploads" description="Open one to continue the review or to write tests for more requirements." />
          <ul className="divide-y divide-border-subtle">
            {sets.data!.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                <FileText className="size-4 text-fg-muted" aria-hidden />
                <Link to={`/projects/${projectId}/requirements/${s.id}`} className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-fg hover:underline">{s.name}</span>
                  <span className="block text-xs text-fg-secondary">
                    {s.sources.filter((x) => x.chars > 0).map((x) => x.name).join(', ')} · {s.createdBy.name} · {timeAgo(s.createdAt)}
                  </span>
                </Link>
                <span className="text-xs text-fg-secondary tabular-nums">
                  {s.counts.requirements} requirements · {s.counts.drafts} test cases · {s.counts.saved} saved
                </span>
                <Badge tone={STATUS_LABEL[s.status].tone}>{STATUS_LABEL[s.status].label}</Badge>
                <button
                  type="button"
                  aria-label={`Delete ${s.name}`}
                  className="text-fg-muted hover:text-danger-text"
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete “${s.name}”?`,
                      message: 'The documents’ text, the requirements and unsaved test cases are deleted. Tests already saved from it stay.',
                      confirmLabel: 'Delete',
                    });
                    if (ok) remove.mutate(s.id);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** /projects/:projectId/requirements (uploads + new) and /projects/:projectId/requirements/:setId (the wizard). */
export function RequirementsPage() {
  const { projectId, setId } = useParams();
  if (!projectId) return null;
  return setId ? <SetWizard key={setId} setId={setId} projectId={projectId} /> : <SetList projectId={projectId} />;
}
